import React, { useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import { Button, Empty, Modal, Popover, Space, Spin, Tag, Typography } from "antd";
import { getCommunityAuditLogs } from "../../api/domainApi";
import { CmntyAuditLogItem } from "../../types/domain";

type TargetType = "COMMENT" | "POST";

/**
 * SWR 캐시 키. 이력을 바꾸는 쪽(잠금·삭제 등)에서 이 키로 무효화한다.
 *
 * 🔴 문자열을 직접 짜맞추지 말 것 — 한 글자만 어긋나도 무효화가 조용히 실패한다.
 */
export const auditLogKey = (targetType: TargetType, targetId: number) =>
  `/community/audit-logs?${targetType}&${targetId}`;

const ACTION_META: Record<string, { label: string; color: string }> = {
  UPDATE: { label: "수정", color: "blue" },
  DELETE: { label: "삭제", color: "red" },
  LOCK: { label: "잠금", color: "volcano" },
  UNLOCK: { label: "잠금 해제", color: "default" },
};

const ts = (v?: string | null) => (v ? v.replace("T", " ").slice(0, 16) : "-");

/** 팝오버 안에서 긴 본문을 2줄로 접는다 — prod 댓글의 24%가 120자를 넘고 최대 886자다. */
const clamp2: React.CSSProperties = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const Meta: React.FC<{ log: CmntyAuditLogItem }> = ({ log }) => {
  const meta = ACTION_META[log.action] ?? { label: log.action, color: "default" };
  return (
    <Space size={6} wrap>
      <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>
        {meta.label}
      </Tag>
      <span style={{ fontSize: 13 }}>{log.actorName ?? "알 수 없음"}</span>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {ts(log.createdAt)}
      </Typography.Text>
    </Space>
  );
};

/** 한 건 렌더. clamped=true 면 본문을 2줄로 접는다(팝오버용). */
const Entry: React.FC<{ log: CmntyAuditLogItem; clamped?: boolean }> = ({
  log,
  clamped,
}) => (
  <div style={{ padding: "8px 0", borderTop: "1px solid #f5f5f5" }}>
    <Meta log={log} />

    {/* 게시글 액션은 본문이 없고 제목만 남는다 */}
    {log.memo && (
      <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>{log.memo}</div>
    )}

    {log.contentBefore != null &&
      (clamped ? (
        <div
          style={{
            ...clamp2,
            fontSize: 13,
            lineHeight: 1.6,
            marginTop: 6,
            color: "#999",
            textDecoration: "line-through",
          }}
        >
          {log.contentBefore}
        </div>
      ) : (
        <div
          style={{
            marginTop: 8,
            padding: "6px 10px",
            background: "#fff2f0",
            borderLeft: "3px solid #ffa39e",
            whiteSpace: "pre-wrap",
            fontSize: 13,
            lineHeight: 1.7,
          }}
        >
          <div style={{ fontSize: 11, color: "#cf1322", marginBottom: 2 }}>
            변경 전
          </div>
          {log.contentBefore}
        </div>
      ))}

    {log.contentAfter != null &&
      (clamped ? (
        <div style={{ ...clamp2, fontSize: 13, lineHeight: 1.6, marginTop: 4 }}>
          {log.contentAfter}
        </div>
      ) : (
        <div
          style={{
            marginTop: 6,
            padding: "6px 10px",
            background: "#f6ffed",
            borderLeft: "3px solid #b7eb8f",
            whiteSpace: "pre-wrap",
            fontSize: 13,
            lineHeight: 1.7,
          }}
        >
          <div style={{ fontSize: 11, color: "#389e0d", marginBottom: 2 }}>
            변경 후
          </div>
          {log.contentAfter}
        </div>
      ))}
  </div>
);

/**
 * 특정 댓글/게시글의 관리자 액션 이력.
 *
 * 🔑 `enabled` 가 false 면 요청 자체를 보내지 않는다(SWR 조건부 키).
 * 팝오버·모달이 닫혀 있는 동안은 호출되지 않으므로, 목록에 표식이 100개 붙어도
 * 실제 요청은 관리자가 누른 그 하나뿐이다.
 */
export const AuditLogTimeline: React.FC<{
  targetType: TargetType;
  targetId: number;
  /** 팝오버용 — 최근 1건만, 본문 2줄 접기 */
  compact?: boolean;
  /** 조회 여부. 닫혀 있으면 false 로 두어 호출을 막는다 */
  enabled?: boolean;
  /** compact 에서 "전체 이력 보기" 를 눌렀을 때 */
  onShowAll?: () => void;
  /** 넘치면 스크롤. 이력이 쌓이면 모달·Drawer 가 한없이 길어진다 */
  maxHeight?: number | string;
}> = ({ targetType, targetId, compact, enabled = true, onShowAll, maxHeight }) => {
  const { data, isLoading } = useSWR(
    enabled ? auditLogKey(targetType, targetId) : null,
    () => getCommunityAuditLogs(targetType, targetId, { size: 20 }),
    {
      // 🔴 전역 SWR 설정(index.tsx)을 여기서만 되돌린다.
      //
      // 전역은 revalidateIfStale=false + dedupingInterval=60초 다. 그건 **대시보드**를 위한
      // 방어책이다 — 한 화면에서 useSWR 9개가 수 초짜리 집계 API 를 동시에 두드려
      // 커넥션 풀을 말린 사고(2026-07-30) 때문에 자동 재검증을 껐다.
      //
      // 이력 조회는 성격이 정반대다. 관리자가 표식을 누른 순간에만 1회 나가고,
      // 인덱스로 몇 행만 읽어 즉시 끝난다. 반면 **방금 고친 내용이 안 보이면 기능이 무의미**하다.
      // (실제로 이 옵션 없이 배포했다가 "새로고침해야 나온다" 는 제보를 받았다.)
      revalidateIfStale: true,
      revalidateOnMount: true,
      dedupingInterval: 0,
      // 전역 keepPreviousData=true 는 재조회 중 옛 이력을 그대로 띄운다.
      // "고쳤는데 안 바뀌었네" 로 보이는 게 이 화면에선 가장 나쁘므로 스피너를 택한다.
      keepPreviousData: false,
    },
  );

  if (isLoading) return <Spin size="small" />;

  const logs = data?.content ?? [];
  // 이력이 없다는 것도 정보다 — 삭제된 댓글에 이력이 없으면 "작성자가 직접 지웠다" 는 뜻이다.
  if (logs.length === 0)
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span style={{ fontSize: 13 }}>
            관리자 조작 이력 없음
            <br />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              작성자가 직접 하셨거나 이력 도입 전 건입니다
            </Typography.Text>
          </span>
        }
      />
    );

  const shown = compact ? logs.slice(0, 1) : logs;
  const rest = logs.length - shown.length;

  return (
    <div>
      <div
        style={
          maxHeight
            ? { maxHeight, overflowY: "auto", paddingRight: 6 }
            : undefined
        }
      >
        {shown.map((log) => (
          <Entry key={log.auditId} log={log} clamped={compact} />
        ))}
      </div>
      {compact && (
        <div
          style={{
            borderTop: "1px solid #f0f0f0",
            paddingTop: 8,
            marginTop: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {rest > 0 ? `이전 이력 ${rest}건 더` : "전체 1건"}
          </Typography.Text>
          <Button type="link" size="small" onClick={onShowAll} style={{ padding: 0 }}>
            전체 이력 보기
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * 목록/스레드에 붙는 표식. 누르면 팝오버 → "전체 이력 보기" 로 모달.
 *
 * 🔑 표식을 그릴지 여부는 호출부가 정한다(`contentModifiedAt` 유무, `delYn` 등).
 * 이 컴포넌트는 <b>눌리기 전까지 아무 요청도 하지 않는다.</b>
 */
export const AuditLogBadge: React.FC<{
  targetType: TargetType;
  targetId: number;
  label: string;
  color?: string;
}> = ({ targetType, targetId, label, color = "gold" }) => {
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const { mutate: globalMutate } = useSWRConfig();

  /**
   * 🔴 열 때마다 **강제로** 다시 받는다.
   *
   * 전역 SWR 이 revalidateIfStale=false + dedupe 60초라(대시보드 커넥션 고갈 대응, index.tsx)
   * 옵션만 지역에서 되돌리면 이미 만들어진 캐시 항목에는 늦게 먹는다. 실제로 그렇게 했다가
   * "고쳤는데 옛날 것만 나온다" 는 제보를 두 번 받았다. mutate 는 명시적 재검증이라
   * 캐시·dedupe 와 무관하게 항상 새로 가져온다.
   */
  const openWith = (v: boolean) => {
    setOpen(v);
    if (v) globalMutate(auditLogKey(targetType, targetId));
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={openWith}
        trigger="click"
        placement="bottomLeft"
        title="변경 이력"
        content={
          <div style={{ width: 380 }}>
            <AuditLogTimeline
              targetType={targetType}
              targetId={targetId}
              compact
              enabled={open}
              onShowAll={() => {
                setOpen(false);
                // 모달도 열기 직전에 최신으로 맞춘다 (팝오버와 같은 이유)
                globalMutate(auditLogKey(targetType, targetId));
                setModalOpen(true);
              }}
            />
          </div>
        }
      >
        <Tag color={color} style={{ cursor: "pointer", marginInlineEnd: 0 }}>
          {label}
        </Tag>
      </Popover>

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={720}
        title={`${targetType === "COMMENT" ? "댓글" : "게시글"} #${targetId} 이력`}
      >
        <AuditLogTimeline
          targetType={targetType}
          targetId={targetId}
          enabled={modalOpen}
          // 이력이 쌓이면(수정 6번이면 6건) 모달이 화면 밖으로 넘어간다
          maxHeight="60vh"
        />
      </Modal>
    </>
  );
};

export default AuditLogTimeline;
